// Charges (migration 050; Mitch, 2026-11-01: no "billion service items, in a long list"): what a maker charges to build
// something that isn't a part with stock (soldering, testing, PCB fab, the components it sources, a stencil per order).
// A BOM family's charges (build_charge) are the plan, counted where services were: the BOM roll-up (shared/boms.ts) and
// the cost stack (shared/costs.ts), each in its layer. A make PO line copies them (po_line_charge) and they're changed to
// that order's quote; the line's price is their total per unit (server/src/mutations/charges.ts).
import { z } from "zod";
import type { BuildCharge, PoLineCharge } from "./db.gen.ts";
import { mulRound } from "./po.ts";

export const CHARGE_PERS = ["unit", "order"] as const;
export type ChargePer = (typeof CHARGE_PERS)[number];
export const CHARGE_PER_LABELS: Record<ChargePer, string> = { unit: "Per unit", order: "Per order" };

/** The cost-stack layers a charge can count in (shared/runs.ts LAYERS without freight and other). */
export const CHARGE_LAYERS = ["materials", "assembly", "packaging", "tooling"] as const;
export type ChargeLayer = (typeof CHARGE_LAYERS)[number];
export const CHARGE_LAYER_LABELS: Record<ChargeLayer, string> = {
  materials: "Materials",
  assembly: "Assembly",
  packaging: "Packaging",
  tooling: "Tooling",
};

/** What a new charge's name suggests, so the usual ones need no choosing. */
export const CHARGE_SUGGESTIONS: { name: string; layer: ChargeLayer; per: ChargePer }[] = [
  { name: "PCB fab", layer: "materials", per: "unit" },
  { name: "Components", layer: "materials", per: "unit" },
  { name: "Soldering (SMT)", layer: "assembly", per: "unit" },
  { name: "Testing", layer: "assembly", per: "unit" },
  { name: "Assembly", layer: "assembly", per: "unit" },
  { name: "Packing", layer: "packaging", per: "unit" },
  { name: "Stencil / setup", layer: "tooling", per: "order" },
];

export type Charge = Pick<BuildCharge, "name" | "per" | "layer"> & { amount: number | string | null };
export type LineCharge = Pick<PoLineCharge, "name" | "per" | "layer"> & { amount: number | string };

const amountOf = (c: { amount: number | string | null }) => (c.amount == null || c.amount === "" ? null : Number(c.amount));

/** One charge per unit when `qty` are made in one order: per-unit as it is, per-order over the quantity. */
export function chargeUnit(c: Charge, qty: number): number | null {
  const a = amountOf(c);
  if (a == null) return null;
  return c.per === "order" ? a / Math.max(1, qty) : a;
}

/**
 * Charges per unit: `unit` adds the per-unit ones (and per-order ones over `qty` when given), `perOrder` the per-order
 * ones (left out of `unit` without a quantity), `missing` the names with no amount yet.
 */
export function chargesTotal(list: readonly Charge[], qty?: number): { unit: number; perOrder: number; missing: string[] } {
  let unit = 0;
  let perOrder = 0;
  const missing: string[] = [];
  for (const c of list) {
    const a = amountOf(c);
    if (a == null) missing.push(c.name);
    else if (c.per === "unit") unit += a;
    else {
      perOrder += a;
      if (qty) unit += a / Math.max(1, qty);
    }
  }
  return { unit, perOrder, missing };
}

/** A make PO line's price from its charges at its quantity, to 4 places (what a PO line keeps). */
export const linePrice = (list: readonly LineCharge[], qty: number) => mulRound(chargesTotal(list, Math.max(1, Number(qty))).unit, 1, 4);

/** Each layer's share of a line's charges at its quantity (adding up to 1), for splitting what was paid. */
export function layerShares(list: readonly LineCharge[], qty: number): Partial<Record<ChargeLayer, number>> {
  const by: Partial<Record<ChargeLayer, number>> = {};
  let total = 0;
  for (const c of list) {
    const u = chargeUnit(c, qty) ?? 0;
    by[c.layer] = (by[c.layer] ?? 0) + u;
    total += u;
  }
  if (total <= 0) return {};
  for (const k of Object.keys(by) as ChargeLayer[]) by[k] = (by[k] as number) / total;
  return by;
}

/** "PCB fab $0.85 · Stencil $40 an order": a list in a line. */
export function chargesText(list: readonly Charge[], money: (n: number) => string): string {
  return list
    .map((c) => {
      const a = amountOf(c);
      return `${c.name} ${a == null ? "(no amount)" : `${money(a)}${c.per === "order" ? " an order" : ""}`}`;
    })
    .join(" · ");
}

const chargeShape = {
  id: z.uuid().optional(),
  name: z.string().trim().min(1, "Name the charge").max(120),
  per: z.enum(CHARGE_PERS),
  layer: z.enum(CHARGE_LAYERS),
};
/** A BOM's charge as the browser sends it (the whole list at once, in order). */
export const buildChargeInput = z.object({
  ...chargeShape,
  amount: z.number().min(0).max(10_000_000).nullable(),
  notes: z.string().trim().max(500).optional(),
});
/** A make PO line's charge: an amount always (in the PO's currency). */
export const lineChargeInput = z.object({ ...chargeShape, amount: z.number().min(0).max(10_000_000) });
